import React, { useState, useRef, useCallback, useEffect } from 'react';
import Papa from 'papaparse';
import { postCompressed } from '@/lib/post-compressed';
import { format } from 'date-fns';
import { Layout } from '@/components/layout';
import { Button } from '@/components/ui-elements';
import { useAuthStore, useAuthHeaders } from '@/lib/auth-store';
import { useToast } from '@/hooks/use-toast';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { cn } from '@/lib/utils';
import {
  Upload, ArrowRight, ArrowLeft, Check, AlertCircle,
  ChevronDown, Loader2, Tag, FileText, Zap,
} from 'lucide-react';
import { ShopifyTargetStoreSelector } from '@/components/shopify-target-store';

function statusBadge(status: string) {
  if (status === 'done') return <span className="px-2 py-0.5 rounded text-xs font-bold bg-emerald-500/10 text-emerald-400 border border-emerald-500/20">Done</span>;
  if (status === 'running') return <span className="px-2 py-0.5 rounded text-xs font-bold bg-amber-500/10 text-amber-400 border border-amber-500/20">Running</span>;
  if (status === 'failed') return <span className="px-2 py-0.5 rounded text-xs font-bold bg-red-500/10 text-red-400 border border-red-500/20">Failed</span>;
  return <span className="px-2 py-0.5 rounded text-xs font-bold bg-slate-500/10 text-muted-foreground border border-slate-500/20">Pending</span>;
}

const BASE = import.meta.env.BASE_URL.replace(/\/$/, '');

interface MetafieldDef {
  namespace: string;
  key: string;
  name: string;
  type: string;
  ownerType: 'PRODUCT' | 'PRODUCTVARIANT';
}

interface MetafieldColMap {
  namespace: string;
  key: string;
  name: string;
  type: string;
  csvColumn: string;
  ownerType: 'PRODUCT' | 'PRODUCTVARIANT';
}

function parseMatrixifyMetafieldHeader(header: string): MetafieldColMap | null {
  const match = header.match(/^(Variant\s+)?Metafield:\s*([^.:\s]+)\.([^\s[]+)\s*\[([^\]]+)\]\s*$/i);
  if (!match) return null;
  return {
    ownerType: match[1] ? 'PRODUCTVARIANT' : 'PRODUCT',
    namespace: match[2]!,
    key: match[3]!,
    name: `${match[2]}.${match[3]}`,
    type: match[4]!,
    csvColumn: header,
  };
}

const IDENTIFIER_OPTIONS = [
  { value: 'handle', label: 'URL Handle', desc: 'Most reliable — unique per product' },
  { value: 'sku',    label: 'Variant SKU', desc: 'Useful if your CSV has a SKU column' },
  { value: 'title',  label: 'Product Title', desc: 'Use only when titles are unique' },
];

export function MetafieldUpdate() {
  const { token } = useAuthStore();
  const authOpts = useAuthHeaders();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const fileRef = useRef<HTMLInputElement>(null);
  const [expandedJobId, setExpandedJobId] = useState<string | null>(null);
  const [dismissingId, setDismissingId] = useState<string | null>(null);

  const [step, setStep] = useState<1 | 2 | 3>(1);
  const [storeId, setStoreId] = useState('');
  const [filename, setFilename] = useState('');
  const [csvText, setCsvText] = useState('');
  const [headers, setHeaders] = useState<string[]>([]);
  const [sampleRows, setSampleRows] = useState<Record<string, string>[]>([]);
  const [totalRows, setTotalRows] = useState(0);
  const [identifierType, setIdentifierType] = useState<'handle' | 'sku' | 'title'>('handle');
  const [identifierColumn, setIdentifierColumn] = useState('');
  const [variantIdentifierColumn, setVariantIdentifierColumn] = useState('');
  const [metaColMaps, setMetaColMaps] = useState<MetafieldColMap[]>([]);
  const [isDragging, setIsDragging] = useState(false);
  const [isParsing, setIsParsing] = useState(false);
  const [isRunning, setIsRunning] = useState(false);
  const [progress, setProgress] = useState<{ current: number; total: number } | null>(null);
  const [result, setResult] = useState<{
    successCount: number; skippedCount: number; notFoundCount: number; errorCount: number;
    errors: { identifier: string; error: string }[];
  } | null>(null);

  const { data: stores = [] } = useQuery<any[]>({
    queryKey: ['stores'],
    queryFn: async () => {
      const res = await fetch(`${BASE}/api/stores`, { headers: authOpts.headers });
      if (!res.ok) throw new Error('Failed to load stores');
      return res.json();
    },
    enabled: !!token,
  });

  const { data: metafieldDefs = [], isLoading: metaDefsLoading } = useQuery<MetafieldDef[]>({
    queryKey: ['metafield-defs', storeId],
    queryFn: async () => {
      const res = await fetch(`${BASE}/api/stores/${storeId}/metafields/definitions`, { headers: authOpts.headers });
      if (!res.ok) return [];
      const data = await res.json();
      return Array.isArray(data) ? data : (data.definitions ?? []);
    },
    enabled: !!storeId && !!token,
    staleTime: 120_000,
  });

  const { data: jobs = [] } = useQuery<any[]>({
    queryKey: ['metafield-update-jobs'],
    queryFn: async () => {
      const res = await fetch(`${BASE}/api/imports/metafields`, { headers: authOpts.headers });
      if (!res.ok) throw new Error('Failed to load history');
      return res.json();
    },
    enabled: !!token,
    refetchInterval: (data) => {
      const hasRunning = (Array.isArray(data) ? data : (data as any)?.state?.data ?? [])
        .some((j: any) => j.status === 'running');
      return hasRunning ? 2000 : false;
    },
  });

  useEffect(() => {
    const mappings = metafieldDefs.map(def => {
      const prefix = def.ownerType === 'PRODUCTVARIANT' ? 'variant metafield' : 'metafield';
      const expected = `${prefix}: ${def.namespace}.${def.key}`.toLowerCase();
      const csvColumn = headers.find(h => h.toLowerCase().startsWith(expected)) ?? '';
      return {
        namespace: def.namespace, key: def.key, name: def.name, type: def.type,
        ownerType: def.ownerType, csvColumn,
      };
    });
    for (const header of headers) {
      const matrixify = parseMatrixifyMetafieldHeader(header);
      if (!matrixify) continue;
      const existing = mappings.find(m =>
        m.ownerType === matrixify.ownerType
        && m.namespace === matrixify.namespace
        && m.key === matrixify.key
      );
      if (existing) {
        existing.csvColumn = header;
      } else {
        mappings.push(matrixify);
      }
    }
    setMetaColMaps(mappings);
  }, [metafieldDefs, headers]);

  // Auto-detect identifier column when headers load
  useEffect(() => {
    if (!headers.length) return;
    const lower = headers.map(h => ({ h, l: h.toLowerCase().replace(/[^a-z0-9]/g, '') }));
    if (identifierType === 'handle') {
      const match = lower.find(x => ['handle', 'urlhandle', 'slug'].includes(x.l));
      if (match) setIdentifierColumn(match.h);
    } else if (identifierType === 'sku') {
      const match = lower.find(x => ['sku', 'variantsku', 'itemcode', 'code'].includes(x.l));
      if (match) setIdentifierColumn(match.h);
    } else {
      const match = lower.find(x => ['title', 'name', 'productname'].includes(x.l));
      if (match) setIdentifierColumn(match.h);
    }
    const variantSku = lower.find(x => ['variantsku', 'sku', 'itemcode', 'code'].includes(x.l));
    if (variantSku) setVariantIdentifierColumn(variantSku.h);
  }, [identifierType, headers]);

  const processFile = useCallback(async (file: File) => {
    setFilename(file.name);
    const text = await file.text();
    setCsvText(text);
    setIsParsing(true);
    try {
      const res = await postCompressed(`${BASE}/api/imports/parse`, { csvText: text, filename: file.name }, { headers: authOpts.headers });
      if (res.status === 413) throw new Error('CSV file is too large. Please split it into smaller files.');
      const data = await res.json().catch(() => ({ error: `Server error (${res.status})` }));
      if (!res.ok) throw new Error(data.error ?? 'Parse failed');
      setHeaders(data.headers);
      setSampleRows(data.sampleRows);
      setTotalRows(data.totalRows);
    } catch (err: any) {
      toast({ title: 'Parse Error', description: err.message, variant: 'destructive' });
    } finally {
      setIsParsing(false);
    }
  }, [authOpts.headers, toast]);

  const handleFile = (file: File) => {
    if (!file.name.endsWith('.csv') && file.type !== 'text/csv') {
      toast({ title: 'Invalid file', description: 'Please upload a CSV file.', variant: 'destructive' });
      return;
    }
    processFile(file);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    const file = e.dataTransfer.files[0];
    if (file) handleFile(file);
  };

  const reset = () => {
    setStep(1);
    setStoreId('');
    setFilename('');
    setCsvText('');
    setHeaders([]);
    setSampleRows([]);
    setTotalRows(0);
    setIdentifierType('handle');
    setIdentifierColumn('');
    setVariantIdentifierColumn('');
    setMetaColMaps([]);
    setResult(null);
    setProgress(null);
    if (fileRef.current) fileRef.current.value = '';
  };

  const runUpdate = async () => {
    setIsRunning(true);
    setProgress(null);
    setResult(null);
    try {
      const res = await postCompressed(`${BASE}/api/imports/metafields/run`, {
        storeId, filename, csvText, identifierType, identifierColumn, variantIdentifierColumn,
        metafieldMappings: metaColMaps.filter(m => m.csvColumn),
      }, { headers: authOpts.headers });
      if (!res.ok || !res.body) {
        const data = await res.json().catch(() => ({ error: `Server error (${res.status})` }));
        throw new Error(data.error ?? 'Update failed');
      }
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n');
        buffer = lines.pop() ?? '';
        for (const line of lines) {
          if (!line.startsWith('data: ')) continue;
          try {
            const event = JSON.parse(line.slice(6));
            if (event.type === 'progress') setProgress({ current: event.current, total: event.total });
            else if (event.type === 'done') {
              setResult({
                successCount: event.successCount,
                skippedCount: event.skippedCount ?? 0,
                notFoundCount: event.notFoundCount,
                errorCount: event.errorCount,
                errors: event.errors,
              });
              queryClient.invalidateQueries({ queryKey: ['metafield-update-jobs'] });
            }
          } catch {}
        }
      }
    } catch (err: any) {
      toast({ title: 'Update Error', description: err.message, variant: 'destructive' });
    } finally {
      setIsRunning(false);
      setProgress(null);
    }
  };

  const dismissJob = async (id: string) => {
    setDismissingId(id);
    try {
      await fetch(`${BASE}/api/imports/metafields/${id}/dismiss`, { method: 'POST', headers: authOpts.headers });
      queryClient.invalidateQueries({ queryKey: ['metafield-update-jobs'] });
    } finally {
      setDismissingId(null);
    }
  };

  const selectedStore = stores.find((s: any) => s.id === storeId);
  const activeMeta = metaColMaps.filter(m => m.csvColumn);
  const activeProductMeta = activeMeta.filter(m => m.ownerType === 'PRODUCT');
  const activeVariantMeta = activeMeta.filter(m => m.ownerType === 'PRODUCTVARIANT');
  const canProceedStep1 = !!storeId && headers.length > 0;
  const canProceedStep2 = activeMeta.length > 0
    && (activeProductMeta.length === 0 || !!identifierColumn)
    && (activeVariantMeta.length === 0 || !!variantIdentifierColumn);

  return (
    <Layout>
      <div className="space-y-8">
        {/* Header */}
        <div>
          <h1 className="text-3xl font-display font-bold text-gradient">Metafield Update</h1>
          <p className="text-muted-foreground mt-1">
            Upload a CSV to write metafield values to existing products and variants — nothing else is touched.
          </p>
        </div>

        {/* Step indicator */}
        <div className="flex items-center gap-2">
          {[
            { n: 1, label: 'Upload CSV' },
            { n: 2, label: 'Map Columns' },
            { n: 3, label: 'Review & Run' },
          ].map(({ n, label }, i) => (
            <React.Fragment key={n}>
              <div className="flex items-center gap-2">
                <div className={cn(
                  "w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold border transition-all",
                  step === n ? "bg-primary text-black border-primary shadow-[0_0_10px_rgba(0,240,255,0.4)]" :
                  step > n ? "bg-primary/20 text-primary border-primary/40" :
                  "bg-muted/50 text-muted-foreground border-border"
                )}>
                  {step > n ? <Check className="w-4 h-4" /> : n}
                </div>
                <span className={cn("text-sm font-medium hidden sm:block", step === n ? "text-foreground" : "text-muted-foreground")}>{label}</span>
              </div>
              {i < 2 && <div className={cn("flex-1 h-px", step > n ? "bg-primary/40" : "bg-muted")} />}
            </React.Fragment>
          ))}
        </div>

        {/* ── Step 1: Store + Upload ── */}
        {step === 1 && (
          <div className="space-y-6">
            {/* Store selector */}
            <div className="glass-panel rounded-2xl p-6 space-y-4">
              {stores.length === 0 ? (
                <p className="text-sm text-muted-foreground">No stores connected. Add a store first.</p>
              ) : (
                <ShopifyTargetStoreSelector
                  stores={stores}
                  value={storeId}
                  onChange={setStoreId}
                  label="Destination Store"
                  variant="tiles"
                />
              )}
            </div>

            {/* File upload */}
            <div className="glass-panel rounded-2xl p-6 space-y-4">
              <h2 className="font-semibold text-foreground">Upload Metafield CSV</h2>
              <div
                onDragOver={e => { e.preventDefault(); setIsDragging(true); }}
                onDragLeave={() => setIsDragging(false)}
                onDrop={handleDrop}
                onClick={() => fileRef.current?.click()}
                className={cn(
                  "relative border-2 border-dashed rounded-2xl p-12 flex flex-col items-center gap-4 cursor-pointer transition-all",
                  isDragging ? "border-primary bg-primary/5" : "border-border hover:border-border hover:bg-muted/50"
                )}
              >
                <input
                  ref={fileRef}
                  type="file"
                  accept=".csv,text/csv"
                  className="hidden"
                  onChange={e => { const f = e.target.files?.[0]; if (f) handleFile(f); }}
                />
                {isParsing ? (
                  <Loader2 className="w-10 h-10 text-primary animate-spin" />
                ) : filename ? (
                  <FileText className="w-10 h-10 text-primary" />
                ) : (
                  <Upload className="w-10 h-10 text-muted-foreground" />
                )}
                <div className="text-center">
                  {isParsing ? (
                    <p className="text-sm text-muted-foreground">Parsing CSV…</p>
                  ) : filename ? (
                    <>
                      <p className="font-medium text-foreground">{filename}</p>
                      <p className="text-sm text-muted-foreground mt-1">{totalRows.toLocaleString()} rows detected</p>
                    </>
                  ) : (
                    <>
                      <p className="font-medium text-foreground">Drop your CSV here or click to browse</p>
                      <p className="text-sm text-muted-foreground mt-1">Supports SoraFlo and Matrixify-style product and variant metafield columns</p>
                    </>
                  )}
                </div>

                {metaColMaps.some(m => m.ownerType === 'PRODUCTVARIANT') && (
                  <div className="flex items-center gap-4 p-3 rounded-xl bg-muted/50 border border-border">
                    <div className="w-48 flex-shrink-0">
                      <p className="text-sm font-medium text-foreground">Variant SKU Column</p>
                      <p className="text-xs text-muted-foreground">Used only for variant metafields</p>
                    </div>
                    <ArrowRight className="w-4 h-4 text-slate-600 flex-shrink-0" />
                    <div className="relative flex-1">
                      <select
                        value={variantIdentifierColumn}
                        onChange={e => setVariantIdentifierColumn(e.target.value)}
                        className="w-full h-9 appearance-none rounded-xl border border-border bg-muted/50 pl-3 pr-8 text-sm text-foreground focus:outline-none focus:ring-1 focus:ring-primary/50 transition-all"
                      >
                        <option value="">— Select column —</option>
                        {headers.map(h => <option key={h} value={h}>{h}</option>)}
                      </select>
                      <ChevronDown className="pointer-events-none absolute right-2 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-muted-foreground" />
                    </div>
                    <div className="w-40 flex-shrink-0 text-xs text-muted-foreground truncate">
                      {variantIdentifierColumn && sampleRows[0] ? `e.g. ${sampleRows[0][variantIdentifierColumn] ?? '–'}` : null}
                    </div>
                  </div>
                )}
              </div>
            </div>

            <div className="flex justify-end">
              <Button onClick={() => setStep(2)} disabled={!canProceedStep1}>
                Map Columns <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </div>
          </div>
        )}

        {/* ── Step 2: Identifier + Metafield mapping ── */}
        {step === 2 && (
          <div className="space-y-6">
            <div className="glass-panel rounded-2xl p-6 space-y-6">

              {/* Product identifier */}
              <div className="space-y-4">
                <div>
                  <h2 className="font-semibold text-foreground">Product Identifier</h2>
                  <p className="text-sm text-muted-foreground mt-0.5">How should we match each CSV row to a Shopify product?</p>
                </div>

                {/* Identifier type */}
                <div className="grid grid-cols-3 gap-3">
                  {IDENTIFIER_OPTIONS.map(opt => (
                    <button
                      key={opt.value}
                      onClick={() => setIdentifierType(opt.value as any)}
                      className={cn(
                        "p-3 rounded-xl border text-left transition-all",
                        identifierType === opt.value
                          ? "border-primary bg-primary/10"
                          : "border-border bg-muted/50 hover:border-border"
                      )}
                    >
                      <p className={cn("text-sm font-medium", identifierType === opt.value ? "text-primary" : "text-foreground")}>{opt.label}</p>
                      <p className="text-xs text-muted-foreground mt-0.5">{opt.desc}</p>
                    </button>
                  ))}
                </div>

                {/* Identifier column picker */}
                <div className="flex items-center gap-4 p-3 rounded-xl bg-muted/50 border border-border">
                  <div className="w-48 flex-shrink-0">
                    <p className="text-sm font-medium text-foreground">
                      {IDENTIFIER_OPTIONS.find(o => o.value === identifierType)?.label} Column
                    </p>
                    <p className="text-xs text-muted-foreground">Which CSV column holds the {identifierType}?</p>
                  </div>
                  <ArrowRight className="w-4 h-4 text-slate-600 flex-shrink-0" />
                  <div className="relative flex-1">
                    <select
                      value={identifierColumn}
                      onChange={e => setIdentifierColumn(e.target.value)}
                      className="w-full h-9 appearance-none rounded-xl border border-border bg-muted/50 pl-3 pr-8 text-sm text-foreground focus:outline-none focus:ring-1 focus:ring-primary/50 transition-all"
                    >
                      <option value="">— Select column —</option>
                      {headers.map(h => <option key={h} value={h}>{h}</option>)}
                    </select>
                    <ChevronDown className="pointer-events-none absolute right-2 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-muted-foreground" />
                  </div>
                  <div className="w-40 flex-shrink-0 text-xs text-muted-foreground truncate">
                    {identifierColumn && sampleRows[0] ? `e.g. ${sampleRows[0][identifierColumn] ?? '–'}` : null}
                  </div>
                </div>
              </div>

              <div className="border-t border-border/50" />

              {/* Metafield mappings */}
              <div className="space-y-4">
                <div className="flex items-center gap-2">
                  <Tag className="w-4 h-4 text-primary/70" />
                  <div>
                    <h2 className="font-semibold text-foreground">Metafield Columns</h2>
                    <p className="text-sm text-muted-foreground">Map each metafield to the CSV column that holds its value.</p>
                  </div>
                </div>

                {metaDefsLoading ? (
                  <div className="flex items-center gap-2 text-sm text-muted-foreground py-4">
                    <Loader2 className="w-4 h-4 animate-spin" /> Loading metafield definitions…
                  </div>
                ) : metaColMaps.length === 0 ? (
                  <div className="flex items-center gap-2 text-sm text-amber-400 bg-amber-500/10 border border-amber-500/20 rounded-xl px-4 py-3">
                    <AlertCircle className="w-4 h-4 flex-shrink-0" />
                    No product or variant metafield definitions found for this store. Create them in Shopify Admin → Settings → Custom data.
                  </div>
                ) : (
                  <div className="space-y-2">
                    {metaColMaps.map((mf, i) => (
                      <div key={`${mf.ownerType}:${mf.namespace}.${mf.key}`} className="flex items-center gap-4 p-3 rounded-xl bg-muted/50 border border-border/50 hover:border-border transition-all">
                        <div className="w-48 flex-shrink-0">
                          <p className="text-sm font-medium text-foreground">{mf.name}</p>
                          <p className="text-xs text-slate-600">{mf.namespace}.{mf.key}</p>
                          <p className="text-[10px] uppercase tracking-wide text-muted-foreground mt-0.5">
                            {mf.ownerType === 'PRODUCTVARIANT' ? 'Variant' : 'Product'}
                          </p>
                        </div>
                        <ArrowRight className="w-4 h-4 text-slate-600 flex-shrink-0" />
                        <div className="relative flex-1">
                          <select
                            value={mf.csvColumn}
                            onChange={e => setMetaColMaps(prev => prev.map((m, j) => j === i ? { ...m, csvColumn: e.target.value } : m))}
                            className="w-full h-9 appearance-none rounded-xl border border-border bg-muted/50 pl-3 pr-8 text-sm text-foreground focus:outline-none focus:ring-1 focus:ring-primary/50 transition-all"
                          >
                            <option value="">— Not mapped —</option>
                            {headers.map(h => <option key={h} value={h}>{h}</option>)}
                          </select>
                          <ChevronDown className="pointer-events-none absolute right-2 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-muted-foreground" />
                        </div>
                        <span className="w-44 flex-shrink-0 text-xs text-slate-600 italic truncate">{mf.type}</span>
                        <div className="w-36 flex-shrink-0 text-xs text-muted-foreground truncate">
                          {mf.csvColumn && sampleRows[0] ? `e.g. ${sampleRows[0][mf.csvColumn] ?? '–'}` : null}
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                {!canProceedStep2 && metaColMaps.length > 0 && (
                  <div className="flex items-center gap-2 text-amber-400 text-sm bg-amber-500/10 border border-amber-500/20 rounded-xl px-4 py-3">
                    <AlertCircle className="w-4 h-4 flex-shrink-0" />
                    Map at least one metafield and select the required product or variant identifier column.
                  </div>
                )}
              </div>
            </div>

            <div className="flex justify-between">
              <Button variant="outline" onClick={() => setStep(1)}>
                <ArrowLeft className="w-4 h-4 mr-2" /> Back
              </Button>
              <Button onClick={() => setStep(3)} disabled={!canProceedStep2}>
                Review & Run <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </div>
          </div>
        )}

        {/* ── Step 3: Review + Run ── */}
        {step === 3 && (
          <div className="space-y-6">
            <div className="glass-panel rounded-2xl p-6 space-y-6">
              <h2 className="font-semibold text-foreground">Review Metafield Update</h2>

              {/* Summary cards */}
              <div className="grid grid-cols-4 gap-4">
                {[
                  { label: 'Store', value: selectedStore?.nickname ?? storeId },
                  { label: 'File', value: filename },
                  { label: 'CSV Rows', value: totalRows.toLocaleString() },
                  { label: 'Metafields to Set', value: `${activeProductMeta.length} product · ${activeVariantMeta.length} variant`, highlight: true },
                ].map(({ label, value, highlight }) => (
                  <div key={label} className={cn("rounded-xl border p-4", highlight ? "bg-primary/5 border-primary/20" : "bg-muted/50 border-border")}>
                    <p className="text-xs text-muted-foreground">{label}</p>
                    <p className={cn("text-sm font-semibold mt-1 truncate", highlight ? "text-primary" : "text-foreground")}>{value}</p>
                  </div>
                ))}
              </div>

              {activeVariantMeta.length > 0 && (
                <div className="rounded-xl bg-muted/50 border border-border p-4">
                  <p className="text-xs text-muted-foreground mb-0.5">Variant identifier</p>
                  <p className="text-sm font-medium text-foreground">
                    Variant SKU
                    <span className="text-muted-foreground font-normal ml-2">← column: <span className="text-primary/80">{variantIdentifierColumn}</span></span>
                  </p>
                </div>
              )}

              {/* Identifier */}
              <div className="rounded-xl bg-muted/50 border border-border p-4 flex items-center gap-4">
                <div>
                  <p className="text-xs text-muted-foreground mb-0.5">Product identifier</p>
                  <p className="text-sm font-medium text-foreground">
                    {IDENTIFIER_OPTIONS.find(o => o.value === identifierType)?.label}
                    <span className="text-muted-foreground font-normal ml-2">← column: <span className="text-primary/80">{identifierColumn}</span></span>
                  </p>
                </div>
              </div>

              {/* Active metafield mappings */}
              <div>
                <p className="text-sm font-medium text-foreground mb-3 flex items-center gap-2">
                  <Tag className="w-4 h-4 text-primary/70" /> Active Metafield Mappings
                </p>
                <div className="grid grid-cols-2 gap-2">
                  {activeMeta.map(mf => (
                    <div key={`${mf.ownerType}:${mf.namespace}.${mf.key}`} className="flex items-center gap-2 text-xs rounded-lg px-3 py-2.5 bg-muted/50 border border-border/50">
                      <Tag className="w-3 h-3 text-primary/50 flex-shrink-0" />
                      <div className="flex-1 min-w-0">
                        <p className="text-primary/80 font-medium truncate">{mf.name} <span className="text-muted-foreground">({mf.ownerType === 'PRODUCTVARIANT' ? 'variant' : 'product'})</span></p>
                        <p className="text-slate-600 truncate">{mf.namespace}.{mf.key}</p>
                      </div>
                      <ArrowLeft className="w-3 h-3 rotate-180 flex-shrink-0 text-slate-600" />
                      <span className="text-muted-foreground truncate max-w-[100px]">{mf.csvColumn}</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Info callout */}
              <div className="flex items-start gap-3 text-sm text-primary bg-primary/5 border border-primary/20 rounded-xl px-4 py-3">
                <Zap className="w-4 h-4 flex-shrink-0 mt-0.5" />
                <span>Only metafield values will be written. No product titles, prices, inventory, or any other data will be changed.</span>
              </div>

              {/* Progress */}
              {isRunning && progress && (
                <div className="space-y-2">
                  <div className="flex justify-between text-xs text-muted-foreground">
                    <span>Processing rows…</span>
                    <span>{progress.current} / {progress.total}</span>
                  </div>
                  <div className="h-2 rounded-full bg-muted/50 overflow-hidden">
                    <div
                      className="h-full rounded-full bg-gradient-to-r from-primary to-emerald-400 transition-all"
                      style={{ width: `${Math.round((progress.current / progress.total) * 100)}%` }}
                    />
                  </div>
                </div>
              )}

              {/* Result */}
              {result && (
                <div className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div className="rounded-xl bg-emerald-500/10 border border-emerald-500/20 p-4">
                      <p className="text-xs text-emerald-400/70">Updated</p>
                      <p className="text-2xl font-bold text-emerald-400 mt-1">{result.successCount}</p>
                    </div>
                    <div className="rounded-xl bg-slate-500/10 border border-slate-500/20 p-4">
                      <p className="text-xs text-muted-foreground/70">Skipped (no mapped values)</p>
                      <p className="text-2xl font-bold text-muted-foreground mt-1">{result.skippedCount}</p>
                    </div>
                    <div className="rounded-xl bg-amber-500/10 border border-amber-500/20 p-4">
                      <p className="text-xs text-amber-400/70">Not Found in Shopify</p>
                      <p className="text-2xl font-bold text-amber-400 mt-1">{result.notFoundCount}</p>
                    </div>
                    <div className="rounded-xl bg-red-500/10 border border-red-500/20 p-4">
                      <p className="text-xs text-red-400/70">Errors</p>
                      <p className="text-2xl font-bold text-red-400 mt-1">{result.errorCount}</p>
                    </div>
                  </div>

                  {result.errors.length > 0 && (
                    <div className="space-y-2">
                      <p className="text-sm font-medium text-foreground">Errors</p>
                      <div className="space-y-1.5 max-h-64 overflow-y-auto pr-1">
                        {result.errors.map((e, i) => (
                          <div key={i} className="flex gap-3 text-xs rounded-lg bg-red-500/5 border border-red-500/20 px-3 py-2">
                            <span className="text-red-400 font-medium flex-shrink-0 truncate max-w-[140px]">{e.identifier}</span>
                            <span className="text-red-700 dark:text-red-300/70 truncate">{e.error}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  <Button onClick={reset} variant="outline" className="w-full">
                    Start New Update
                  </Button>
                </div>
              )}
            </div>

            {!result && (
              <div className="flex justify-between">
                <Button variant="outline" onClick={() => setStep(2)} disabled={isRunning}>
                  <ArrowLeft className="w-4 h-4 mr-2" /> Back
                </Button>
                <Button onClick={runUpdate} disabled={isRunning}>
                  {isRunning
                    ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Running…</>
                    : <><Zap className="w-4 h-4 mr-2" /> Run Metafield Update</>
                  }
                </Button>
              </div>
            )}
          </div>
        )}
        {/* Update History */}
        {jobs.length > 0 && (
          <div className="glass-panel rounded-2xl p-6 space-y-4">
            <h2 className="font-semibold text-foreground">Update History</h2>
            <div className="space-y-2">
              {jobs.map((job: any) => {
                const isExpanded = expandedJobId === job.id;
                const jobErrors: { identifier: string; error: string }[] = Array.isArray(job.errors) ? job.errors : [];
                return (
                  <div key={job.id} className={cn("rounded-xl border transition-all overflow-hidden", job.status === 'running' ? "bg-primary/5 border-primary/20" : "bg-muted/50 border-border/50")}>
                    <div className="p-4 flex items-center gap-4">
                      <FileText className={cn("w-5 h-5 flex-shrink-0", job.status === 'running' ? "text-primary" : "text-muted-foreground")} />
                      <div className="flex-1 overflow-hidden">
                        <p className="text-sm font-medium text-foreground truncate">{job.filename}</p>
                        <p className="text-xs text-muted-foreground">{job.storeName} · {format(new Date(job.createdAt), 'dd MMM yyyy HH:mm')}</p>
                      </div>

                      {/* Completed stats */}
                      {job.status !== 'running' && (
                        <div className="flex items-center gap-4 text-xs text-muted-foreground flex-shrink-0">
                          <span className="text-emerald-400 font-medium">{job.successCount} updated</span>
                          {job.skippedCount > 0 && <span>{job.skippedCount} skipped</span>}
                          {job.notFoundCount > 0 && <span className="text-amber-400">{job.notFoundCount} not found</span>}
                          {jobErrors.length > 0 && (
                            <button
                              onClick={() => setExpandedJobId(isExpanded ? null : job.id)}
                              className="flex items-center gap-1 text-red-400 hover:text-red-700 dark:hover:text-red-300 transition-colors"
                            >
                              {jobErrors.length} issues
                              <ChevronDown className={cn("w-3 h-3 transition-transform", isExpanded && "rotate-180")} />
                            </button>
                          )}
                        </div>
                      )}

                      {/* Running state */}
                      {job.status === 'running' && (
                        <>
                          <span className="text-xs text-primary font-medium tabular-nums flex-shrink-0">
                            {(job.processedCount ?? 0).toLocaleString()} / {job.totalRows.toLocaleString()}
                          </span>
                          {!isRunning && (
                            <button
                              onClick={() => dismissJob(job.id)}
                              disabled={dismissingId === job.id}
                              className="flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground border border-border hover:border-border rounded-lg px-2.5 py-1 transition-all flex-shrink-0 disabled:opacity-50 disabled:pointer-events-none"
                              title="Mark this stuck job as done"
                            >
                              {dismissingId === job.id ? <Loader2 className="w-3 h-3 animate-spin" /> : 'Dismiss'}
                            </button>
                          )}
                        </>
                      )}

                      {statusBadge(job.status)}
                    </div>

                    {/* Running progress bar */}
                    {job.status === 'running' && job.totalRows > 0 && (
                      <div className="px-4 pb-4 space-y-1">
                        <div className="w-full h-1.5 rounded-full bg-muted overflow-hidden">
                          <div
                            className="h-full rounded-full bg-primary transition-all duration-500"
                            style={{ width: `${Math.round(((job.processedCount ?? 0) / job.totalRows) * 100)}%` }}
                          />
                        </div>
                        <p className="text-xs text-muted-foreground text-right">
                          {Math.round(((job.processedCount ?? 0) / job.totalRows) * 100)}% complete
                        </p>
                      </div>
                    )}

                    {/* Expanded error list */}
                    {isExpanded && jobErrors.length > 0 && (
                      <div className="px-4 pb-4 border-t border-border pt-3 space-y-1.5 max-h-64 overflow-y-auto">
                        {jobErrors.map((e, i) => (
                          <div key={i} className="flex gap-3 text-xs rounded-lg bg-red-500/5 border border-red-500/20 px-3 py-2">
                            <span className="text-red-400 font-medium flex-shrink-0 truncate max-w-[160px]">{e.identifier}</span>
                            <span className="text-red-700 dark:text-red-300/70 truncate">{e.error}</span>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
}
